import React from 'react';
import {
    Autocomplete, Button, Typography, Grid, Stack, Chip, FormControl, FormControlLabel, FormHelperText,
    FormLabel, Radio, RadioGroup
} from '@mui/material';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux'
import CssTextField from '../../../overrides/longSearchField';
import CloseButton from '../../../overrides/closeButton';
import { getTodayInputDate } from '../../../../utils/common';
import { saveCutoffScheduleWatcher } from '../../../../redux/actionCreators/admin/cutoffLunchMenu';
import useCutoffItemOptions from './useCutoffItemOptions';
import { REPEAT, REPEAT_OPTIONS, WEEK_DAYS, repeatOf, daysFor } from './scheduleUtils';

/*
  A cutoff schedule: the same fields as a cutoff, plus when it runs.
    Automatic - today's cutoff is created by the server every due day (00:05),
                once, daily, Mon-Fri or on chosen days, between two dates.
    Manual    - a saved template; "Generate today" on the list creates today's.
*/
function CutoffScheduleCreation(props) {
    const dispatch = useDispatch()
    const { setOpen, editData } = props;

    const today = getTodayInputDate();
    const initialRepeat = repeatOf(editData)

    const notPast = (value, original) => !value || value >= today || (editData && value === original)

    const Schema = Yup.object().shape({
        mode: Yup.string().oneOf(['auto', 'manual']).required(),
        repeat: Yup.string(),
        onceDate: Yup.string().when(['mode', 'repeat'], {
            is: (mode, repeat) => mode === 'auto' && repeat === REPEAT.ONCE,
            then: (s) => s.required('Date is required')
                .test('not-past', 'Date cannot be in the past', (v) => notPast(v, editData?.StartDate)),
        }),
        startDate: Yup.string().when(['mode', 'repeat'], {
            is: (mode, repeat) => mode === 'auto' && repeat !== REPEAT.ONCE,
            then: (s) => s.required('Start date is required')
                .test('not-past', 'Start date cannot be in the past', (v) => notPast(v, editData?.StartDate)),
        }),
        endDate: Yup.string()
            .test('after-start', 'End date must be on or after the start date', function (value) {
                const { mode, repeat, startDate } = this.parent
                if (mode !== 'auto' || repeat === REPEAT.ONCE || !value || !startDate) return true
                return value >= startDate
            }),
        customDays: Yup.number()
            .test('some-day', 'Pick at least one day', function (value) {
                const { mode, repeat } = this.parent
                return mode !== 'auto' || repeat !== REPEAT.CUSTOM || value > 0
            }),
        startTime: Yup.string().required('Start time is required'),
        endTime: Yup.string()
            .required('End time is required')
            .test('after-start', 'End time must be after start time', function (value) {
                const { startTime } = this.parent
                if (!startTime || !value) return true
                return value > startTime
            }),
        lunchCount: Yup.number()
            .typeError('Lunch count must be a number')
            .integer('Lunch count must be a whole number')
            .min(1, 'Lunch count must be greater than zero')
            .required('Lunch count is required'),
        userType: Yup.number().min(1, 'Type is required').required('Type is required'),
        vendorId: Yup.number().min(1, 'Vendor is required').required('Vendor is required'),
        menuItemId: Yup.number().min(1, 'Menu item is required').required('Menu item is required'),
    });

    const formik = useFormik({
        initialValues: {
            mode: editData && !editData.AutoGenerate ? 'manual' : 'auto',
            repeat: initialRepeat,
            onceDate: initialRepeat === REPEAT.ONCE ? editData.StartDate : today,
            startDate: editData?.StartDate || today,
            endDate: initialRepeat !== REPEAT.ONCE ? (editData?.EndDate || '') : '',
            customDays: initialRepeat === REPEAT.CUSTOM ? +editData.RepeatDays : 0,
            startTime: editData?.StartTime || '',
            endTime: editData?.EndTime || '',
            lunchCount: editData?.LunchCount ?? '',
            userType: editData?.UserType || '',
            vendorId: editData?.VendorId || '',
            menuItemId: editData?.MenuItemId || '',
            menuItemName: editData?.MenuItemName || '',
            menuId: editData?.MenuId || '',
        },

        validationSchema: Schema,

        onSubmit: (v) => {
            const auto = v.mode === 'auto'
            const once = v.repeat === REPEAT.ONCE

            dispatch(saveCutoffScheduleWatcher({
                ACTION: editData ? 'UPDATE' : 'INSERT',
                NID: editData?.nID,
                STARTTIME: v.startTime,
                ENDTIME: v.endTime,
                LUNCHCOUNT: +v.lunchCount,
                MENUITEMID: +v.menuItemId,
                MENUITEMNAME: v.menuItemName,
                MENUID: +v.menuId || null,
                VENDORIDS: `${v.vendorId}`,
                USERTYPE: +v.userType,
                AUTOGENERATE: auto,
                REPEATDAYS: auto ? daysFor(v.repeat, v.customDays) : null,
                STARTDATE: auto ? (once ? v.onceDate : v.startDate) : null,
                ENDDATE: auto ? (once ? v.onceDate : (v.endDate || null)) : null,
            }, setOpen))
        }
    });

    const { errors, touched, handleSubmit, getFieldProps, setFieldValue, values } = formik;

    const {
        typeOptions, vendorOptions, menuItemOptions, selectedType, selectedVendor, selectedMenuItem,
        handleTypeChange, handleVendorChange, handleMenuItemChange
    } = useCutoffItemOptions(values, setFieldValue)

    const auto = values.mode === 'auto'
    const selectedRepeat = REPEAT_OPTIONS.find((r) => r.id === values.repeat) || null

    const toggleDay = (bit) => setFieldValue('customDays', values.customDays ^ bit)

    const half = { lg: 6, md: 6, sm: 6, xs: 12, item: true }

    const dateField = (name, label, extra = {}) => (
        <CssTextField
            required={!extra.optional}
            fullWidth={true}
            label={label}
            name={name}
            color='primary'
            type='date'
            variant='outlined'
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: extra.min }}
            {...getFieldProps(name)}
            error={Boolean(touched[name] && errors[name])}
            helperText={(touched[name] && errors[name]) || extra.hint}
        />
    )

    return (
        <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Typography variant='h5' align='left' style={{ paddingBottom: '10px' }}>
                    {editData ? 'Cutoff Schedule Update' : 'Cutoff Schedule Creation'}
                </Typography>

                <Grid container style={{ paddingTop: '10px' }} spacing={3} direction='row'>
                    <Grid xs={12} item={true}>
                        <FormControl>
                            <FormLabel id='cutoff-schedule-mode'>
                                <Typography variant='t14_400'>Create the cutoff</Typography>
                            </FormLabel>
                            <RadioGroup row aria-labelledby='cutoff-schedule-mode' {...getFieldProps('mode')}>
                                <FormControlLabel value='auto' control={<Radio />} label='Automatically on schedule' />
                                <FormControlLabel value='manual' control={<Radio />} label='Manually (Generate today)' />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {auto &&
                        <Grid {...half}>
                            <Autocomplete
                                fullWidth
                                disableClearable
                                options={REPEAT_OPTIONS}
                                value={selectedRepeat}
                                size="medium"
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(event, value) => setFieldValue('repeat', value ? value.id : REPEAT.DAILY)}
                                getOptionLabel={(option) => option.name || ''}
                                renderInput={(params) => <CssTextField {...params} label='Repeat' name='repeat' required={true} />}
                            />
                        </Grid>
                    }

                    {auto && values.repeat === REPEAT.ONCE &&
                        <Grid {...half}>{dateField('onceDate', 'Date', { min: today })}</Grid>
                    }

                    {auto && values.repeat !== REPEAT.ONCE &&
                        <>
                            <Grid {...half}>{dateField('startDate', 'Start Date', { min: today })}</Grid>
                            <Grid {...half}>{dateField('endDate', 'End Date', { min: values.startDate || today, optional: true, hint: 'Leave empty to keep repeating' })}</Grid>
                        </>
                    }

                    {auto && values.repeat === REPEAT.CUSTOM &&
                        <Grid {...half}>
                            <Stack direction='row' flexWrap='wrap' gap={1} sx={{ pt: 1 }}>
                                {WEEK_DAYS.map((d) => {
                                    const on = (values.customDays & d.bit) !== 0
                                    return <Chip key={d.bit} label={d.label} clickable
                                        color={on ? 'primary' : 'default'}
                                        variant={on ? 'filled' : 'outlined'}
                                        onClick={() => toggleDay(d.bit)} />
                                })}
                            </Stack>
                            {touched.customDays && errors.customDays
                                ? <FormHelperText error>{errors.customDays}</FormHelperText> : null}
                        </Grid>
                    }

                    <Grid {...half}>
                        <CssTextField
                            required={true}
                            fullWidth={true}
                            label='Start Time'
                            name='startTime'
                            color='primary'
                            type='time'
                            variant='outlined'
                            InputLabelProps={{ shrink: true }}
                            {...getFieldProps("startTime")}
                            error={Boolean(touched.startTime && errors.startTime)}
                            helperText={touched.startTime && errors.startTime}
                        />
                    </Grid>

                    <Grid {...half}>
                        <CssTextField
                            required={true}
                            fullWidth={true}
                            label='End Time'
                            name='endTime'
                            color='primary'
                            type='time'
                            variant='outlined'
                            InputLabelProps={{ shrink: true }}
                            {...getFieldProps("endTime")}
                            error={Boolean(touched.endTime && errors.endTime)}
                            helperText={touched.endTime && errors.endTime}
                        />
                    </Grid>

                    <Grid {...half}>
                        <CssTextField
                            required={true}
                            fullWidth={true}
                            placeholder='Lunch Count'
                            label='Lunch Count (per day)'
                            name='lunchCount'
                            color='primary'
                            type='number'
                            variant='outlined'
                            inputProps={{ min: 1 }}
                            {...getFieldProps("lunchCount")}
                            error={Boolean(touched.lunchCount && errors.lunchCount)}
                            helperText={touched.lunchCount && errors.lunchCount}
                        />
                    </Grid>

                    <Grid {...half}>
                        <Autocomplete
                            fullWidth
                            options={typeOptions}
                            value={selectedType}
                            size="medium"
                            autoHighlight
                            isOptionEqualToValue={(option, value) => +option.id === +value.id}
                            onChange={(event, value) => handleTypeChange(value)}
                            getOptionLabel={(option) => option.name || ''}
                            renderInput={(params) => <CssTextField {...params}
                                label='Select Type'
                                name='userType'
                                required={true}
                                error={Boolean(touched.userType && errors.userType)}
                                helperText={touched.userType && errors.userType}
                            />}
                        />
                    </Grid>

                    <Grid {...half}>
                        <Autocomplete
                            fullWidth
                            options={vendorOptions}
                            value={selectedVendor}
                            size="medium"
                            autoHighlight
                            isOptionEqualToValue={(option, value) => +option.id === +value.id}
                            onChange={(event, value) => handleVendorChange(value)}
                            getOptionLabel={(option) => option.name || ''}
                            renderOption={(optionProps, option) => (
                                <li {...optionProps} key={option.id}>{option.name}</li>
                            )}
                            renderInput={(params) => <CssTextField {...params}
                                label='Vendor'
                                name='vendorId'
                                required={true}
                                error={Boolean(touched.vendorId && errors.vendorId)}
                                helperText={touched.vendorId && errors.vendorId}
                            />}
                        />
                    </Grid>

                    <Grid {...half}>
                        <Autocomplete
                            fullWidth
                            disabled={!selectedVendor || !selectedType}
                            options={menuItemOptions}
                            value={selectedMenuItem}
                            size="medium"
                            autoHighlight
                            isOptionEqualToValue={(option, value) => +option.nID === +value.nID}
                            onChange={(event, value) => handleMenuItemChange(value)}
                            getOptionLabel={(option) => option.cItemName || ''}
                            renderOption={(optionProps, option) => (
                                <li {...optionProps} key={option.nID}>{option.cItemName}</li>
                            )}
                            renderInput={(params) => <CssTextField {...params}
                                label='Menu Item'
                                name='menuItemId'
                                required={true}
                                error={Boolean(touched.menuItemId && errors.menuItemId)}
                                helperText={
                                    (touched.menuItemId && errors.menuItemId)
                                    || (!selectedType ? 'Select a type first' : '')
                                    || (!selectedVendor ? 'Select a vendor first' : '')
                                }
                            />}
                        />
                    </Grid>

                    <Grid xs={12} item={true}>
                        <Typography variant='t14_400' color='grey.600'>
                            {auto
                                ? "The server creates the day's cutoff at 00:05. If today is a scheduled day and the window hasn't ended, today's cutoff is created as soon as you save."
                                : 'Nothing runs by itself. Press "Generate today" on the schedule whenever you want today\'s cutoff.'}
                        </Typography>
                    </Grid>

                    <Grid xs={12} item={true}>
                        <Stack spacing={2} direction='row' justifyContent='end' >
                            <CloseButton onClick={() => setOpen(false)}
                                sx={{ height: '42px', minWidth: '120px', marginTop: 'auto' }}
                                variant='contained'
                                size='medium'
                                type='button'>
                                <Typography variant='t14_400' color='common.black'>Cancel</Typography>
                            </CloseButton>
                            <Button onClick={handleSubmit}
                                style={{ height: '42px', minWidth: '120px', marginTop: 'auto' }}
                                variant='contained'
                                size='medium'
                                type='submit'
                            >
                                <Typography variant='t14_400' color='grey.0'>Submit</Typography>
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Form>
        </FormikProvider>
    );
}

export default CutoffScheduleCreation;
